/**
 * Hedged execution across providers (plan §4).
 *
 *   1. Rank the non-last-resort providers by score (./health.ts) and fire the leader with a 2500 ms
 *      per-attempt timeout (capped by the time left).
 *   2. If the leader is still unresolved at 250 ms, fire the runner-up: the best-scored healthy
 *      free provider. A provider from a different family is preferred (a same-family pick is
 *      scored ×2); a same-family runner-up is allowed (HEDGE_SAME_FAMILY) because the two free
 *      Instagram lanes share one backend estate yet run independent sessions (plan §4 family
 *      policy, 2026-10-31). First VALIDATED success wins; the loser is aborted.
 *   3. A fast failure fails over to the next candidate at once (no waiting for the 250 ms).
 *   4. Last-resort providers (StarAPI, paid) are only reached after every free provider failed or
 *      was unavailable, and only while ≥ 800 ms of the budget is left. Never hedged. With
 *      `reservePaid` (profile lookups) a sequential free failover only starts when the paid lane
 *      still fits after that failover's full attempt cap: a free lane that hangs (the estate-a
 *      lanes hang on a handle that does not exist) can no longer eat the time the paid confirmer needs.
 *      With `reservePaid`, a free lane's "no such account" also dispatches the paid confirmer at once
 *      (beside any hanging free lane), uncapped up to the overall deadline; two agreeing misses conclude.
 *   5. Hard overall budget 4000 ms: no attempt may outlive it.
 *
 * Success = validated success (./shapes.ts validateResult), never a bare 2xx: an invalid body is a
 * failure sample and the next candidate runs. A valid empty search is an "empty" answer: it is only
 * returned once a second provider agrees (or nothing failed) — an outage never masquerades as absence.
 * Every terminal outcome feeds ./health.ts (EWMA + breaker); cancelled losers, skipped providers
 * (budget shed) and authoritative misses are not failures; a Retry-After opens the breaker for
 * that long. At most 4 calls per free lane (8 per RapidAPI lane) are in flight per isolate.
 *
 * 🔴 Browser+edge-safe: fetch / AbortController / setTimeout only.
 */
import {
	acquire as acquireSlot,
	effectiveState,
	isHealthy,
	loadHealth,
	recordFailure,
	recordSuccess,
	score,
	type HealthState,
} from './health';
import { ProviderMiss, ProviderSkip, type Provider, type ProviderRequest } from './registry';
import { validateResult } from './shapes';
import { getToken, invalidateToken } from './tokens';

/** Instagram budget (plan §3.5): identical for every free provider. The lane estate answers in 1.2-4.4 s
 *  (re-measured 2026-10-31 04:15Z), so a 2.5 s cap was cutting the slower lane off mid-answer; the hedge
 *  still returns the faster one as soon as it wins. */
export const ATTEMPT_TIMEOUT_MS = 3_500;
export const HEDGE_DELAY_MS = 250;
export const OVERALL_BUDGET_MS = 5_000;
/** The paid last resort (StarAPI) is slower than the free lanes (measured 2.5 s) and gets its own attempt cap. */
export const LAST_RESORT_ATTEMPT_MS = 3_000;

/** Per-platform budget. TikTok's RapidAPI lanes are slow (measured 4.2 s and 7.5 s) and share one
 *  family/quota, so their attempts get 5.5 s of a 6 s budget and are tried one after the other (no hedge). */
export const PLATFORM_BUDGET = {
	instagram: { attemptMs: ATTEMPT_TIMEOUT_MS, overallMs: OVERALL_BUDGET_MS, hedge: true },
	tiktok: { attemptMs: 5_500, overallMs: 6_000, hedge: false },
} as const;
export const budgetFor = (platform: Provider['platform']) => PLATFORM_BUDGET[platform] ?? PLATFORM_BUDGET.instagram;
/** The hedge may pick a runner-up from the leader's own family when no other family is healthy. */
export const HEDGE_SAME_FAMILY = true;
/** Do not start a free attempt with less budget than this left … */
const MIN_FREE_BUDGET_MS = 300;
/** … or a paid last-resort attempt with less than this. */
const MIN_PAID_BUDGET_MS = 800;
const MAX_IN_FLIGHT_FREE = 4;
const MAX_IN_FLIGHT_RAPIDAPI = 8;

export interface Attempt {
	providerId: string;
	/** ms after the call started */
	startedMs: number;
	ms: number;
	outcome: 'ok' | 'miss' | 'empty' | 'invalid' | 'fail' | 'timeout' | 'skipped' | 'cancelled' | 'denied';
	error?: string;
}

export interface HedgeResult<T = unknown> {
	/** false = authoritative "no such account" (profile) or a confirmed empty search. */
	found: boolean;
	result: T | undefined;
	providerId: string | null;
	attempts: Attempt[];
	/** A runner-up was fired at the hedge delay (the hedge actually happened). */
	hedgeFired?: boolean;
	/** Provider calls actually started (cancelled hedge losers included; `attempts` only lists finished ones). */
	launched?: number;
}

export class RouterFailure extends Error {
	attempts: Attempt[];
	/** True when a paid last-resort call was dispatched (so a caller must not repeat it elsewhere). */
	paidDispatched: boolean;
	hedgeFired: boolean;
	launched: number;
	/** True when the overall budget ran out (deadline hit, a candidate left untried for lack of time, or under the 800 ms
	 *  paid floor left), as opposed to every candidate tried with time to spare or none being available. */
	budgetExhausted: boolean;
	constructor(message: string, attempts: Attempt[], paidDispatched = false, hedgeFired = false, launched = 0, budgetExhausted = false) {
		super(message);
		this.name = 'RouterFailure';
		this.attempts = attempts;
		this.paidDispatched = paidDispatched;
		this.hedgeFired = hedgeFired;
		this.launched = launched;
		this.budgetExhausted = budgetExhausted;
	}
}

/** A lane answered 429/503 with a Retry-After: the breaker honours it. */
export class RateLimited extends Error {
	retryAfterMs: number;
	constructor(retryAfterMs: number) {
		super(`rate limited (Retry-After ${Math.round(retryAfterMs / 1000)}s)`);
		this.name = 'RateLimited';
		this.retryAfterMs = retryAfterMs;
	}
}

export interface HedgeOptions {
	attemptTimeoutMs?: number;
	hedgeDelayMs?: number;
	overallBudgetMs?: number;
	sameFamilyHedge?: boolean;
	/** Fire a runner-up at the hedge delay. Default: Instagram yes, TikTok no. */
	hedge?: boolean;
	/** A runner-up whose calls are metered (RapidAPI transport: every started call is billed, a cancelled hedge loser
	 *  included) is only started once the leader has gone this long without an answer. Default: `hedgeDelayMs`. */
	meteredHedgeDelayMs?: number;
	/** Keep room for the paid last resort: a sequential free failover only starts when the paid lane still fits after
	 *  the failover's full attempt cap (otherwise the paid lane goes first). Default off; Instagram profiles turn it on. */
	reservePaid?: boolean;
}

/** Injection points for tests; production uses the defaults. */
export interface HedgeDeps {
	run?: (provider: Provider, req: ProviderRequest, signal: AbortSignal) => Promise<unknown>;
	loadHealth?: (list: Provider[]) => Promise<Map<string, HealthState>>;
	acquire?: (provider: Provider) => Promise<boolean>;
	onSuccess?: (provider: Provider, latencyMs: number) => void;
	onFailure?: (provider: Provider, cooldownMs?: number) => void;
	now?: () => number;
}

// ── generic executor ────────────────────────────────────────────────────────────────

function abortError(): Error {
	const e = new Error('aborted');
	e.name = 'AbortError';
	return e;
}

/** Rejects as soon as `signal` aborts, whatever `promise` is doing (covers class-based lanes whose fetch has no signal). */
function raceAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
	if (signal.aborted) {
		promise.catch(() => {});
		return Promise.reject(abortError());
	}
	return new Promise<T>((resolve, reject) => {
		const onAbort = () => reject(abortError());
		signal.addEventListener('abort', onAbort, { once: true });
		promise.then(
			(v) => { signal.removeEventListener('abort', onAbort); resolve(v); },
			(e) => { signal.removeEventListener('abort', onAbort); reject(e); }
		);
	});
}

/** Run one provider: its custom `execute`, or the generic fetch + parse (with one 401 re-login for Bearer lanes). */
export async function executeProvider(provider: Provider, req: ProviderRequest, signal: AbortSignal): Promise<unknown> {
	if (provider.execute) return await raceAbort(provider.execute(req, signal), signal);
	if (!provider.request) throw new Error(`${provider.id}: provider has neither request nor execute`);

	const attemptReq: ProviderRequest = { ...req };
	for (let pass = 0; pass < 2; pass++) {
		if (provider.requiresToken) attemptReq.token = await raceAbort(getToken(provider, signal), signal);
		const built = provider.request(attemptReq);
		const res = await fetch(built.url, { ...built.init, signal });
		if (res.status === 401 && provider.requiresToken && pass === 0) {
			await invalidateToken(provider, attemptReq.token);
			continue;
		}
		// "no such account" statuses only mean that for lookups of one handle, never for a search endpoint
		if (req.capability !== 'search_users' && provider.missStatuses?.includes(res.status)) throw new ProviderMiss();
		if (res.status === 429 || res.status === 503) {
			const seconds = Number(res.headers.get('retry-after'));
			if (Number.isFinite(seconds) && seconds > 0) throw new RateLimited(seconds * 1000);
		}
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		const text = await raceAbort(res.text(), signal);
		let body: unknown = text;
		try { body = JSON.parse(text); } catch { /* parser decides what non-JSON means */ }
		return provider.parser(body, attemptReq);
	}
	throw new Error(`${provider.id}: unauthorized after re-login`);
}

// ── the hedge ───────────────────────────────────────────────────────────────────────

type Outcome =
	| { kind: 'ok'; value: unknown }
	| { kind: 'miss' | 'empty' | 'cancelled' }
	| { kind: 'skip' | 'invalid' | 'fail' | 'timeout'; error: string; cooldownMs?: number; truncated?: boolean };

interface Active {
	provider: Provider;
	ctrl: AbortController;
	startedAt: number;
	cancelled: boolean;
}

type Event =
	| { type: 'done'; active: Active; outcome: Outcome; ms: number; promise: Promise<Event> }
	| { type: 'hedge' }
	| { type: 'deadline' };

const inFlight = new Map<string, number>();
const capFor = (p: Provider) => (p.family === 'rapidapi' ? MAX_IN_FLIGHT_RAPIDAPI : MAX_IN_FLIGHT_FREE);

export async function hedged<T = unknown>(
	pool: Provider[],
	req: ProviderRequest,
	opts: HedgeOptions = {},
	deps: HedgeDeps = {}
): Promise<HedgeResult<T>> {
	const budget = budgetFor(pool[0]?.platform ?? 'instagram');
	const attemptTimeoutMs = opts.attemptTimeoutMs ?? budget.attemptMs;
	/** Per-attempt cap: the paid last resort has its own (never above what the caller asked for). */
	const attemptCapFor = (p: Provider) => (p.lastResort ? Math.min(opts.attemptTimeoutMs ?? LAST_RESORT_ATTEMPT_MS, LAST_RESORT_ATTEMPT_MS) : attemptTimeoutMs);
	const hedgeDelayMs = opts.hedgeDelayMs ?? HEDGE_DELAY_MS;
	const meteredHedgeDelayMs = Math.max(hedgeDelayMs, opts.meteredHedgeDelayMs ?? hedgeDelayMs);
	const hedgeEnabled = opts.hedge ?? budget.hedge;
	const overallMs = opts.overallBudgetMs ?? budget.overallMs;
	const sameFamilyHedge = opts.sameFamilyHedge ?? HEDGE_SAME_FAMILY;
	const reservePaid = opts.reservePaid ?? false;
	const run = deps.run ?? executeProvider;
	const acquire = deps.acquire ?? ((p: Provider) => acquireSlot(p, req.capability));
	const onSuccess = deps.onSuccess ?? ((p: Provider, ms: number) => recordSuccess(p, req.capability, ms));
	const onFailure = deps.onFailure ?? ((p: Provider, cooldown?: number) => recordFailure(p, req.capability, cooldown));
	const now = deps.now ?? (() => Date.now());

	const t0 = now();
	const deadline = t0 + overallMs;
	const attempts: Attempt[] = [];
	let paidDispatched = false;
	let hedgeFiredFlag = false;
	let launched = 0;
	const fail = (message: string, budgetExhausted = false): never => { throw new RouterFailure(message, attempts, paidDispatched, hedgeFiredFlag, launched, budgetExhausted); };

	if (!pool.length) fail('no providers available');

	const health = await (deps.loadHealth ?? ((list: Provider[]) => loadHealth(list, req.capability)))(pool);
	const rank = (list: Provider[], leaderFamily: Provider['family'] | null): Provider[] => {
		const t = now();
		return list
			.map((p) => ({ p, s: score(health.get(p.id)!, p.family, leaderFamily, t) }))
			.filter((x) => Number.isFinite(x.s))
			.sort((a, b) => a.s - b.s)
			.map((x) => x.p);
	};

	let free = rank(pool.filter((p) => !p.lastResort), null);
	let last = rank(pool.filter((p) => p.lastResort), null);

	/** Take the next admitted provider from a list (dropping ones whose probe slot or concurrency cap is taken). */
	const takeNext = async (list: Provider[], accept: (p: Provider) => boolean = () => true): Promise<Provider | null> => {
		for (let i = 0; i < list.length; i++) {
			const p = list[i];
			if (!accept(p)) continue;
			list.splice(i, 1);
			i--;
			// reserve the concurrency slot synchronously (before any await) so parallel calls can't all pass the check
			if ((inFlight.get(p.id) ?? 0) < capFor(p)) {
				inFlight.set(p.id, (inFlight.get(p.id) ?? 0) + 1);
				if (await acquire(p)) return p;
				inFlight.set(p.id, Math.max(0, (inFlight.get(p.id) ?? 1) - 1));
			}
			attempts.push({ providerId: p.id, startedMs: now() - t0, ms: 0, outcome: 'denied' });
		}
		return null;
	};

	const pending = new Set<Promise<Event>>();
	const actives = new Set<Active>();
	let answers = 0; // providers that answered "no such account" / "no results"
	let paidMiss = false; // the paid last resort answered "no such account"
	const familyOf = new Map(pool.map((p) => [p.id, p.family] as const));
	let hedgeUsed = false;
	let leaderFamily: Provider['family'] | null = null;
	const timers: ReturnType<typeof setTimeout>[] = [];

	/** `capMs` overrides the lane's own attempt cap (the paid miss confirmer runs to the overall deadline). */
	const launch = (provider: Provider, capMs: number = attemptCapFor(provider)): void => {
		const startedAt = now();
		const timeoutMs = Math.max(1, Math.min(capMs, deadline - startedAt));
		const ctrl = new AbortController();
		const active: Active = { provider, ctrl, startedAt, cancelled: false };
		actives.add(active);
		launched++;
		if (provider.lastResort) paidDispatched = true;
		/** The overall deadline (not the lane's own cap) cut this attempt short: no verdict on the lane. */
		const truncated = timeoutMs < capMs;
		let timedOut = false;
		const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, timeoutMs);
		const promise: Promise<Event> = (async (): Promise<Outcome> => {
			try {
				const value = await run(provider, { ...req }, ctrl.signal);
				const verdict = validateResult(req, value);
				if (verdict === 'ok') return { kind: 'ok', value };
				if (verdict === 'empty') return { kind: 'empty' };
				return { kind: 'invalid', error: 'response failed validation' };
			} catch (error: any) {
				if (error instanceof ProviderMiss) return { kind: 'miss' };
				if (error instanceof ProviderSkip) return { kind: 'skip', error: error.message };
				if (active.cancelled) return { kind: 'cancelled' };
				if (error instanceof RateLimited) return { kind: 'fail', error: error.message, cooldownMs: error.retryAfterMs };
				const message = String(error?.message || error).slice(0, 160);
				return timedOut
					? { kind: 'timeout', error: `timeout after ${timeoutMs} ms`, truncated }
					: { kind: 'fail', error: message };
			} finally {
				clearTimeout(timer);
				inFlight.set(provider.id, Math.max(0, (inFlight.get(provider.id) ?? 1) - 1));
			}
		})().then((outcome): Event => ({ type: 'done', active, outcome, ms: now() - startedAt, promise }));
		pending.add(promise);
	};

	const cancelAll = () => {
		for (const a of actives) {
			a.cancelled = true;
			a.ctrl.abort();
		}
	};
	const cleanup = () => { for (const t of timers) clearTimeout(t); };

	/** A lane is only worth starting when its typical latency (EWMA, capped at its own attempt cap) fits in what is left. */
	const fits = (p: Provider, left: number): boolean => left >= Math.max(MIN_FREE_BUDGET_MS, Math.min(health.get(p.id)!.ewma, attemptCapFor(p)));

	/** What the paid lane needs to start: the same test as `fits`, with the 800 ms paid floor. */
	const paidNeed = (p: Provider): number => Math.max(MIN_PAID_BUDGET_MS, Math.min(health.get(p.id)!.ewma, attemptCapFor(p)));

	/** Launch the next candidate: free providers first; last-resort only once no free one fits, and only with >= 800 ms left.
	 *  With `reservePaid`, a free failover must also leave the paid lane its room after the failover's full attempt cap. */
	const launchNext = async (): Promise<boolean> => {
		const left = deadline - now();
		free = rank(free, leaderFamily);
		const reserve = reservePaid && last.length ? Math.min(...last.map(paidNeed)) : 0;
		const freeFits = (c: Provider) => fits(c, left) && (reserve === 0 || left - attemptCapFor(c) >= reserve);
		let p = await takeNext(free, freeFits);
		if (!p && !free.some(freeFits) && left >= MIN_PAID_BUDGET_MS) p = await takeNext(last, (c) => fits(c, left));
		// the paid lane could not be admitted (or did not fit after all): the reserve is moot, use the time on a free lane
		if (!p && reserve) p = await takeNext(free, (c) => fits(c, left));
		if (!p) return false;
		launch(p);
		return true;
	};

	const deadlinePromise = new Promise<Event>((resolve) => {
		timers.push(setTimeout(() => resolve({ type: 'deadline' }), overallMs + 50));
	});

	/** The "nothing answered" verdict once no candidate is left. A confirmed absence needs two agreeing providers, or
	 *  one whose every attempted peer also answered: a failed, skipped or denied peer is not agreement. */
	const conclude = (): HedgeResult<T> => {
		const unanswered = attempts.filter((a) => a.outcome !== 'ok' && a.outcome !== 'miss' && a.outcome !== 'empty' && a.outcome !== 'cancelled').length;
		if (answers >= 2 || (answers >= 1 && unanswered === 0))
			return { found: false, result: undefined, providerId: null, attempts, hedgeFired: hedgeFiredFlag, launched };
		// Profiles: the paid lane's "no such account" stands when every other lane that did not answer either hung
		// (an estate-a lane hangs on a handle that does not exist, it does not say so) or never ran. Without it a missing
		// handle behind two hanging lanes was a RouterFailure ("try again" + a false provider-outage alert) instead of
		// "check the username". A lane that failed outright (HTTP error, invalid body, rate limit) still blocks the verdict.
		if (reservePaid && paidMiss && attempts.every((a) => a.outcome === 'ok' || a.outcome === 'miss' || a.outcome === 'empty'
			|| a.outcome === 'cancelled' || a.outcome === 'denied' || a.outcome === 'skipped'
			|| (a.outcome === 'timeout' && familyOf.get(a.providerId) === 'estate-a')))
			return { found: false, result: undefined, providerId: null, attempts, hedgeFired: hedgeFiredFlag, launched };
		// Out of time: a candidate still listed here was not started only because it no longer fitted, or the attempts
		// used the budget down to below the paid floor. (Every candidate tried with time to spare is not exhaustion.)
		const exhausted = free.length + last.length > 0 || deadline - now() < MIN_PAID_BUDGET_MS;
		return fail(attempts.length ? `all providers failed: ${attempts.map((a) => `${a.providerId}=${a.outcome}`).join(', ')}` : 'no attempt could start', exhausted);
	};

	try {
		// 1. leader
		const leader = (await takeNext(free)) ?? (!free.length && overallMs >= MIN_PAID_BUDGET_MS ? await takeNext(last) : null);
		if (!leader) return fail('every provider is unavailable (breaker open, probe in flight or at capacity)');
		leaderFamily = leader.family;
		launch(leader);
		free = rank(free, leaderFamily);

		let hedgePromise: Promise<Event> | null = null;
		const leaderAt = now();
		const armHedge = (delayMs: number) => new Promise<Event>((resolve) => {
			timers.push(setTimeout(() => resolve({ type: 'hedge' }), delayMs));
		});
		if (hedgeEnabled && !leader.lastResort && free.length) {
			hedgePromise = armHedge(hedgeDelayMs);
		} else {
			hedgeUsed = true;
		}

		// 2. event loop
		for (;;) {
			const racers: Promise<Event>[] = [...pending, deadlinePromise];
			if (hedgePromise && !hedgeUsed) racers.push(hedgePromise);
			const ev = await Promise.race(racers);

			if (ev.type === 'deadline') {
				cancelAll();
				return fail(`overall budget of ${overallMs} ms exhausted`, true);
			}

			if (ev.type === 'hedge') {
				// a closed lane, or a half-open one whose single probe lease we win (so a recovered lane re-enters without a total failover)
				const hedgeable = (p: Provider, left: number) => (sameFamilyHedge || p.family !== leaderFamily) && fits(p, left)
					&& (isHealthy(health.get(p.id)!, now()) || effectiveState(health.get(p.id)!, now()) === 'half-open');
				// a metered runner-up waits until the leader has had `meteredHedgeDelayMs` (re-arm the hedge for the rest)
				const meteredWait = leaderAt + meteredHedgeDelayMs - now();
				if (meteredWait > 0 && pending.size === 1 && free.find((p) => hedgeable(p, deadline - now()))?.transport === 'rapidapi') {
					hedgePromise = armHedge(meteredWait);
					continue;
				}
				hedgeUsed = true;
				if (pending.size === 1 && deadline - now() >= MIN_FREE_BUDGET_MS) {
					const left = deadline - now();
					const runnerUp = await takeNext(free, (p) => hedgeable(p, left));
					if (runnerUp) { hedgeFiredFlag = true; launch(runnerUp); }
				}
				continue;
			}

			// a provider finished
			pending.delete(ev.promise);
			actives.delete(ev.active);
			const { provider } = ev.active;
			const o = ev.outcome;
			const record: Attempt = { providerId: provider.id, startedMs: ev.active.startedAt - t0, ms: ev.ms, outcome: o.kind === 'skip' ? 'skipped' : o.kind };
			if ('error' in o) record.error = o.error;
			attempts.push(record);

			if (o.kind === 'ok') {
				onSuccess(provider, ev.ms);
				cancelAll();
				return { found: true, result: o.value as T, providerId: provider.id, attempts, hedgeFired: hedgeFiredFlag, launched };
			}
			if (o.kind === 'miss' || o.kind === 'empty') {
				onSuccess(provider, ev.ms); // a valid answer: the lane is healthy
				answers++;
				if (o.kind === 'miss' && provider.lastResort) paidMiss = true;
				// With `reservePaid` (profiles) a free lane's "no such account" is confirmed by the paid lane AT ONCE, beside any
				// free lane still hanging, and it may run to the overall deadline (or the caller's own attempt cap): StarAPI's miss
				// takes ~3 s, so behind a hanging lane under its 3 s cap it timed out, the paid call made the failure final (an
				// error instead of "not found") and the timeouts opened its breaker (1 Nov, 5 of 12 live misses).
				if (reservePaid && o.kind === 'miss' && answers < 2 && !paidDispatched && last.length && deadline - now() >= MIN_PAID_BUDGET_MS) {
					const left = deadline - now();
					const confirmer = await takeNext(last, (c) => fits(c, left));
					if (confirmer) {
						hedgeUsed = true; // the confirmer takes the hedge's place
						launch(confirmer, opts.attemptTimeoutMs ?? Number.POSITIVE_INFINITY);
					}
				}
			} else if (o.kind === 'fail' || o.kind === 'timeout' || o.kind === 'invalid') {
				// a timeout caused only by the overall deadline says nothing about the lane
				if (!(o.kind === 'timeout' && o.truncated)) onFailure(provider, 'cooldownMs' in o ? o.cooldownMs : undefined);
			}

			// Profiles: two agreeing answers conclude even while a free lane still hangs (the confirmer ran beside it).
			if (reservePaid && answers >= 2) return conclude();
			if (pending.size === 0) {
				hedgeUsed = true; // a failover replaces the pending hedge
				// Two independent providers agreeing on "nothing" is the answer: don't walk the rest of the list.
				if (answers >= 2) return conclude();
				if (!(await launchNext())) return conclude();
			}
		}
	} finally {
		cleanup();
		cancelAll(); // belt and braces: nothing may outlive the call
	}
}
